/**
 * TIROIR PANIER — ouverture, contenu, et l'ajout sans rechargement.
 *
 * ── Pourquoi ce fichier existe à côté de panier.js ────────────────────────
 *
 * panier.js appartient à la page /cart : il ne connaît que la racine
 * [data-pn] et ses commandes. Le tiroir, lui, est monté dans le groupe du
 * pied de page et vit sur toutes les pages. Deux objets, deux fichiers —
 * et surtout deux histoires qui ne se marchent pas dessus au prochain
 * changement de l'un ou de l'autre.
 *
 * Les deux se partagent le travail sans se croiser :
 *   panier.js  →  les lignes de la page /cart          ([data-pn-*])
 *   tiroir.js  →  le tiroir, et l'ajout au panier      ([data-tiroir])
 *
 * ── Ce fichier n'est jamais un passage obligé ─────────────────────────────
 *
 * Sans lui, le bouton du header reste un lien vers /cart, les quantités du
 * tiroir restent des formulaires vers /cart/change, « Retirer » reste un
 * lien, et « Ajouter au panier » reste un envoi de formulaire. On ne fait
 * qu'éviter le rechargement.
 *
 * ── Les choix qui ne vont pas de soi ──────────────────────────────────────
 *
 * 1. Le HTML du tiroir n'est jamais reconstruit ici. On redemande la
 *    section à Shopify (API Sections) et on échange ses deux blocs
 *    internes. Le Liquid reste l'unique auteur du markup : prix, remises de
 *    lot et devises restent ceux du serveur.
 *
 * 2. On échange .ct-in et .ct-foot, pas le <dialog> lui-même : remplacer
 *    l'élément le fermerait au moment précis où on veut l'ouvrir.
 *
 * 3. Sur la page /cart, l'ajout ne fait pas surgir le tiroir. Le visiteur y
 *    est déjà : recouvrir la page qu'il regarde par un panneau qui dit la
 *    même chose n'aide personne.
 *
 * 4. Le tiroir se reconnaît à `data-tiroir`, jamais à `data-panier` — ce
 *    dernier est déjà porté par le bouton d'ajout de la fiche produit, et
 *    sur une page produit il vient avant le pied de page dans le document.
 */

const tiroir = document.querySelector('[data-tiroir]');

if (tiroir) {
  const racine = window.Shopify?.routes?.root || '/';
  const idSection = tiroir.dataset.section;
  /* Sur /cart, la page fait déjà le travail du tiroir. */
  const surPagePanier = window.location.pathname.endsWith('/cart');

  const ouvrir = () => {
    if (!tiroir.open) tiroir.showModal();
  };

  /* ── rafraîchissement ────────────────────────────────────────────────── */

  const rafraichir = async () => {
    tiroir.setAttribute('aria-busy', 'true');
    try {
      const reponse = await fetch(`${racine}?sections=${idSection}`);
      if (!reponse.ok) return false;
      const donnees = await reponse.json();
      const frais = new DOMParser().parseFromString(donnees[idSection], 'text/html');
      const neuf = frais.querySelector('[data-tiroir]');
      if (!neuf) return false;

      for (const partie of ['.ct-in', '.ct-foot']) {
        const avant = tiroir.querySelector(partie);
        const apres = neuf.querySelector(partie);
        if (avant && apres) avant.replaceWith(apres);
        else if (avant) avant.remove();
        else if (apres) tiroir.append(apres);
      }

      /* Le compteur du header suit le tiroir : une seule source, celle du
         serveur. Le cœur des favoris porte aussi un .hdr-count — on ne vise
         que le bouton du panier. */
      const nombre = tiroir.querySelector('[data-nb]')?.textContent.trim() ?? '0';
      document.querySelectorAll('[data-panier-ouvrir]').forEach((bouton) => {
        const pastille = bouton.querySelector('.hdr-count');
        if (pastille) {
          pastille.textContent = nombre;
          pastille.hidden = nombre === '0';
        }
      });
      return true;
    } catch {
      return false;
    } finally {
      tiroir.removeAttribute('aria-busy');
    }
  };

  /* ── ouverture et fermeture ──────────────────────────────────────────────
     Échap, le voile et le piège à focus viennent de <dialog>. Reste le clic
     sur le fond : il vise le <dialog> lui-même, jamais son contenu. */
  document.addEventListener('click', (evenement) => {
    if (evenement.target.closest('[data-panier-ouvrir]')) {
      evenement.preventDefault();
      ouvrir();
      return;
    }
    if (evenement.target.closest('[data-panier-fermer]')) tiroir.close();
    else if (evenement.target === tiroir) tiroir.close();
  });

  /* ── quantités et retrait, à l'intérieur du tiroir ───────────────────── */

  const changer = async (cle, quantite) => {
    tiroir.setAttribute('aria-busy', 'true');
    await fetch(`${racine}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: cle, quantity: quantite }),
    });
    await rafraichir();
  };

  tiroir.addEventListener('click', async (evenement) => {
    const lien = evenement.target.closest('[data-retirer]');
    if (!lien) return;
    evenement.preventDefault();
    const cle = lien.closest('[data-ligne]')?.dataset.cle;
    if (cle) await changer(cle, 0);
  });

  /* Le formulaire de ligne porte la clé, le bouton porte la nouvelle
     quantité : rien à recalculer côté client. */
  tiroir.addEventListener('submit', async (evenement) => {
    const formulaire = evenement.target.closest('.ct-qty');
    if (!formulaire) return;
    evenement.preventDefault();
    const bouton = evenement.submitter;
    const cle = formulaire.querySelector('[name="id"]')?.value;
    if (bouton && cle) await changer(cle, Number(bouton.value));
  });

  /* ── ajout au panier ─────────────────────────────────────────────────────
     Délégué au document : les formulaires d'ajout vivent sur la fiche
     produit, dans le tiroir, sur les pages de collection et dans les
     articles du Mag. */
  document.addEventListener('submit', async (evenement) => {
    const formulaire = evenement.target;
    if (!(formulaire instanceof HTMLFormElement)) return;
    if (!formulaire.action.includes('/cart/add')) return;

    evenement.preventDefault();
    const bouton = evenement.submitter;
    if (bouton) bouton.disabled = true;

    try {
      const reponse = await fetch(`${racine}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(formulaire),
      });

      if (!reponse.ok) {
        /* Épuisé, quantité indisponible… On laisse le navigateur envoyer le
           formulaire : Shopify affiche son propre message, en français et à
           jour. Le réécrire ici, c'est se donner un second endroit où il
           peut devenir faux. */
        formulaire.submit();
        return;
      }

      await rafraichir();

      /* `data-ajout-discret` : le formulaire confirme sur place au lieu
         d'ouvrir le tiroir. C'est le comportement du prototype pour les
         compléments — et la lecture d'un article ne doit pas être coupée
         par un panneau. */
      const discret = bouton?.closest('[data-ajout-discret]');
      if (discret && bouton) {
        const texte = bouton.textContent;
        bouton.textContent = tiroir.dataset.ajoute || texte;
        setTimeout(() => {
          bouton.textContent = texte;
          bouton.disabled = false;
        }, 1600);
        return;
      }

      if (!surPagePanier) ouvrir();
      else window.location.reload();
    } finally {
      /* Les boutons du tiroir sont remplacés par le rafraîchissement ;
         ceux de la fiche produit ne le sont pas. */
      if (bouton && !bouton.closest('[data-ajout-discret]')) bouton.disabled = false;
    }
  });
}
